import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MotifCard,
  MotifCardBody,
  MotifIconButton,
  MotifIcon,
} from "@/libs/@ey-xd/motif-react";
import {
  IconoirEditPencil,
  IconoirTrash,
  IconoirArrowDown,
  IconoirArrowUp,
} from "@ey-xd/motif-icon";
import "./Stages.css";

import { StageType, OrderChange, PositionType } from "./StagesType";
import CreateEditStage from "./CreateEditStage";
import DeleteStageStatus from "./DeleteStageStatus";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import { havingDeletePermission, havingEditPermission } from "@/utility/common";
import CommonTooltipWrapper from "@/components/Tooltip/CommonTooltipWrapper";

export default function StageTile({
  stageData,
  selectedStage,
  setSelectedStage,
  getStageData,
  handleOrderChange,
  ClaimStageDefinition,
  setShowToast,
  setShowDeleteSuccessMessage,
  showDeleteSuccessMessage,
  index,
}: {
  stageData: StageType;
  selectedStage: string;
  setSelectedStage: (stageId: string) => void;
  getStageData: (isCreate: boolean) => void;
  handleOrderChange: (change: OrderChange, id: string) => void;
  ClaimStageDefinition: string[];
  setShowToast?: any;
  setShowDeleteSuccessMessage: any;
  showDeleteSuccessMessage: any;
  index: number;
}) {
  const { t } = useTranslation();
  const [showCreateStage, setShowCreateStage] = useState(false);
  const [showDeleteStage, setShowDeleteStage] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState({
    type: "",
    message: "",
    show: false,
  });
  const [deleteEntityName, setDeleteEntityName] = useState<any>("");

  return (
    <>
      <div id={stageData.id} className="stage-tile-container">
        <MotifCard
          className={
            "stage-tile" + (selectedStage === stageData.id ? " active" : "")
          }
          orientation="horizontal"
          variant="card-border"
        >
          

          {deleteErrorMessage.show && (
            <NotificationGrid
              show={true}
              message={deleteErrorMessage.message}
              type={deleteErrorMessage.type}
              scrollToTop={false}
              onClose={() => {
                setDeleteErrorMessage({ type: "", message: "", show: false });
              }}
            />
          )}
          <MotifCardBody className="stage-tile-body">
            <div
              className="stage-tile-header"
              onClick={() => {
                setSelectedStage(stageData.id!);
                setShowToast({
                  type: "",
                  message: "",
                  show: true,
                  variant: "",
                });
                setShowDeleteSuccessMessage({
                  ...showDeleteSuccessMessage,
                  index: "",
                  show: false,
                  message: "",
                  type: "",
                });
              }}
            >
              <span className="motif-body1-default-bold">{stageData.name}</span>
              <span>
                {stageData.position === PositionType.DebtorPosition
                  ? t("pages.configuration.stages.debtorPosition")
                  : t("pages.configuration.stages.creditorPosition")}
              </span>
            </div>
            <div className="stage-tile-actions">
              <div className="action-icons">
                {havingDeletePermission(ClaimStageDefinition) && (
                  <CommonTooltipWrapper
                    tooltipTitle={t("global.globalActions.delete")}
                  >
                    <MotifIconButton
                      onClick={() => {
                        setShowDeleteStage(true);
                        setDeleteEntityName(stageData?.name);
                        setShowToast({
                          type: "",
                          message: "",
                          show: true,
                          variant: "",
                        })
                        setShowDeleteSuccessMessage({
                          ...showDeleteSuccessMessage,
                          index: index - 1,
                          show: false,
                          message: "",
                          type: "",
                        });
                      }}
                      type="button"
                    >
                      <MotifIcon iconFunction={IconoirTrash} />
                    </MotifIconButton>
                  </CommonTooltipWrapper>
                )}
                <CommonTooltipWrapper
                  tooltipTitle={t("global.globalActions.edit")}
                >
                  <MotifIconButton
                    onClick={() => setShowCreateStage(true)}
                    type="button"
                  >
                    <MotifIcon iconFunction={IconoirEditPencil} />
                  </MotifIconButton>
                </CommonTooltipWrapper>
              </div>
              {havingEditPermission(ClaimStageDefinition) && (
                <div className="arrow-icons">
                  <CommonTooltipWrapper
                    tooltipTitle={t("global.globalActions.moveUp")}
                  >
                    <MotifIconButton
                      onClick={() =>
                        handleOrderChange(OrderChange.Up, stageData.id!)
                      }
                      type="button"
                      size="small"
                    >
                      <MotifIcon iconFunction={IconoirArrowUp} />
                    </MotifIconButton>
                  </CommonTooltipWrapper>
                  <CommonTooltipWrapper
                    tooltipTitle={t("global.globalActions.moveDown")}
                  >
                    <MotifIconButton
                      onClick={() =>
                        handleOrderChange(OrderChange.Down, stageData.id!)
                      }
                      type="button"
                      size="small"
                    >
                      <MotifIcon iconFunction={IconoirArrowDown} />
                    </MotifIconButton>
                  </CommonTooltipWrapper>
                </div>
              )}
            </div>
          </MotifCardBody>
        </MotifCard>
        {showDeleteSuccessMessage.show && showDeleteSuccessMessage.variant === "stage" &&
            showDeleteSuccessMessage.index === index && (
              <><br></br><NotificationGrid
                show={true}
                message={showDeleteSuccessMessage.message}
                type={showDeleteSuccessMessage.type}
                scrollToTop={false}
                onClose={() =>
                  setShowDeleteSuccessMessage({
                    ...showDeleteSuccessMessage,
                    index: "",
                    show: false,
                    message: "",
                    type: "",
                  })
                }
              />
              </>
            )}
      </div>

      <CreateEditStage
        ClaimStageDefinition={ClaimStageDefinition}
        show={showCreateStage}
        setShow={setShowCreateStage}
        stageData={stageData}
        isEdit={true}
        getStageData={getStageData}
        setUpdateMessage={setDeleteErrorMessage}
      />

      <DeleteStageStatus
        show={showDeleteStage}
        setShow={setShowDeleteStage}
        id={stageData.id}
        getStageData={getStageData}
        isStage={true}
        selectedStage={selectedStage}
        setSelectedStage={setSelectedStage}
        setDeleteErrorMessage={setDeleteErrorMessage}
        deleteEntityName={deleteEntityName}
        setShowToast={setShowToast}
        showDeleteSuccessMessage={showDeleteSuccessMessage}
        setShowDeleteSuccessMessage={setShowDeleteSuccessMessage}
      />
    </>
  );
}
